import React, { useState } from 'react';
import './CharacterGenerator.css';
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';

const genderOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non-binary', label: 'Non-Binary' },
];

const skinToneOptions = [
  { value: 'light', label: 'Light' },
  { value: 'light-brown', label: 'Light Brown' },
  { value: 'medium', label: 'Medium' },
  { value: 'dark', label: 'Dark' },
];

const eyeColorOptions = [
  { value: 'brown', label: 'Brown' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'hazel', label: 'Hazel' },
  { value: 'gray', label: 'Gray' },
];

const faceStyleOptions = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'stylized', label: 'Stylized' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'anime', label: 'Anime' },
];

const expressionOptions = [
  { value: 'soft-smile', label: 'Soft Smile' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'serious', label: 'Serious' },
  { value: 'happy', label: 'Happy' },
  { value: 'sad', label: 'Sad' },
];

const hairStyleOptions = [
  { value: 'short-curly', label: 'Short & Curly' },
  { value: 'long-straight', label: 'Long & Straight' },
  { value: 'medium-wavy', label: 'Medium & Wavy' },
  { value: 'bald', label: 'Bald' },
  { value: 'short-straight', label: 'Short & Straight' },
];

const outfitStyleOptions = [
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'business', label: 'Business' },
  { value: 'athletic', label: 'Athletic' },
];

const backgroundOptions = [
  { value: 'studio', label: 'Studio' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'urban', label: 'Urban' },
  { value: 'nature', label: 'Nature' },
];

const lightingOptions = [
  { value: 'soft-natural', label: 'Soft Natural' },
  { value: 'dramatic', label: 'Dramatic' },
  { value: 'studio', label: 'Studio' },
  { value: 'golden-hour', label: 'Golden Hour' },
];

function CharacterGenerator() {
  const [gender, setGender] = useState('female');
  const [age, setAge] = useState('70');
  const [skinTone, setSkinTone] = useState('light-brown');
  const [eyeColor, setEyeColor] = useState('brown');
  const [faceStyle, setFaceStyle] = useState('photorealistic');
  const [expression, setExpression] = useState('soft-smile');
  const [hairStyle, setHairStyle] = useState('short-curly');
  const [outfitStyle, setOutfitStyle] = useState('casual');
  const [background, setBackground] = useState('studio');
  const [lighting, setLighting] = useState('soft-natural');
  const [extraNotes, setExtraNotes] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [error, setError] = useState('');
  const [candidateImage, setCandidateImage] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [characterName, setCharacterName] = useState('');

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      setError('');
      setCandidateImage(null);

      const params = {
        gender, age, skinTone, eyeColor, faceStyle,
        expression, hairStyle, outfitStyle, background, lighting, extraNotes,
      };

      const result = await influencerService.generateCharacter(params);

      if (result.success) {
        const candidate = {
          url: getInfluencerImageUrl(result.path),
          prompt: result.enhanced_prompt,
          filename: result.filename,
        };
        setCandidateImage(candidate);
        setEnhancedPrompt(result.enhanced_prompt);
        setShowNameDialog(true);
      } else {
        setError('Failed to generate character');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate character');
      console.error('Generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!candidateImage || !characterName.trim()) return;

    try {
      const result = await influencerService.saveCharacter(
        candidateImage.filename,
        characterName.trim()
      );

      if (result) {
        const newImageUrl = getInfluencerImageUrl(result.path);
        setGeneratedImages((prev) => [newImageUrl, ...prev]);
        await influencerService.cleanupTempFiles([candidateImage.filename]);
        setCandidateImage(null);
        setShowNameDialog(false);
        setCharacterName('');
      }
    } catch (err) {
      setError('Failed to save character');
      console.error('Save error:', err);
    }
  };

  return (
    <div className="character-generator">
      <div className="header">
        <h1>Create Your Character</h1>
        <p className="subtitle">Design AI characters with balanced controls for better results</p>
      </div>

      <div className="character-layout">
        {/* Left - Form */}
        <div className="character-form-panel">
          <div className="form-card">
            {/* Basic Info */}
            <div className="form-section">
              <h3 className="section-title">Basic Info</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Gender</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)}>
                    {genderOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Age</label>
                  <input type="text" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Enter age" />
                </div>
                <div className="form-group">
                  <label>Skin Tone</label>
                  <select value={skinTone} onChange={(e) => setSkinTone(e.target.value)}>
                    {skinToneOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Eye Color</label>
                  <select value={eyeColor} onChange={(e) => setEyeColor(e.target.value)}>
                    {eyeColorOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Appearance Style */}
            <div className="form-section">
              <h3 className="section-title">Appearance Style</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Face Style</label>
                  <select value={faceStyle} onChange={(e) => setFaceStyle(e.target.value)}>
                    {faceStyleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Expression</label>
                  <select value={expression} onChange={(e) => setExpression(e.target.value)}>
                    {expressionOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Hair & Outfit */}
            <div className="form-section">
              <h3 className="section-title">Hair & Outfit</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Hair Style</label>
                  <select value={hairStyle} onChange={(e) => setHairStyle(e.target.value)}>
                    {hairStyleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Outfit Style</label>
                  <select value={outfitStyle} onChange={(e) => setOutfitStyle(e.target.value)}>
                    {outfitStyleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Scene & Mood */}
            <div className="form-section">
              <h3 className="section-title">Scene & Mood</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Background</label>
                  <select value={background} onChange={(e) => setBackground(e.target.value)}>
                    {backgroundOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Lighting Mood</label>
                  <select value={lighting} onChange={(e) => setLighting(e.target.value)}>
                    {lightingOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Extra Notes */}
            <div className="form-section">
              <div className="form-group">
                <label>Extra Notes (Optional)</label>
                <textarea
                  value={extraNotes}
                  onChange={(e) => setExtraNotes(e.target.value)}
                  placeholder="Accessories, mood, references..."
                  rows="2"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button
                className="xbtn xbtn-md xbtn-primary"
                onClick={handleGenerate}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <><i className="fas fa-spinner fa-spin"></i> Generating...</>
                ) : (
                  <><i className="fas fa-magic"></i> Generate Character</>
                )}
              </button>
            </div>

            {error && (
              <div className="message error">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {enhancedPrompt && (
              <div className="enhanced-prompt">
                <strong>Enhanced Prompt:</strong>
                <p>{enhancedPrompt}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right - Preview */}
        <div className="character-preview-panel">
          <h2>
            {candidateImage ? 'Generated Character' : `Generated Characters (${generatedImages.length})`}
          </h2>

          {isGenerating && (
            <div className="preview-placeholder">
              <i className="fas fa-spinner fa-spin fa-3x"></i>
              <p>Generating your character...</p>
              <small>This may take 60-90 seconds</small>
            </div>
          )}

          {!isGenerating && !candidateImage && generatedImages.length === 0 && (
            <div className="preview-placeholder">
              <i className="fas fa-user-plus fa-3x"></i>
              <p>No characters generated yet</p>
              <small>Click "Generate Character" to create your first character</small>
            </div>
          )}

          {candidateImage && (
            <div className="candidate-preview">
              <img src={candidateImage.url} alt="Generated character" />
            </div>
          )}

          {!candidateImage && generatedImages.length > 0 && (
            <div className="saved-grid">
              {generatedImages.map((src, index) => (
                <div key={index} className="saved-image-card">
                  <img src={src} alt={`Saved character ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Name Dialog */}
      {showNameDialog && (
        <div className="modal-overlay" onClick={() => setShowNameDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Name Your Character</h3>
            <p className="modal-desc">Give your character a name to save it to your library.</p>
            <div className="modal-field">
              <label>Name</label>
              <input
                type="text"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                placeholder="e.g., Sarah Professional"
                onKeyPress={(e) => e.key === 'Enter' && handleSave()}
              />
            </div>
            <div className="modal-actions">
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setShowNameDialog(false)}>Cancel</button>
              <button className="xbtn xbtn-md xbtn-primary" onClick={handleSave} disabled={!characterName.trim()}>
                Save Character
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CharacterGenerator;
